/// <amd-module name="Wit/UI/Components/WorkItemTreeGrid/WorkItemTreeGrid.Props" />
import { ObservableValue } from "VSS/Core/Observable";
import { WorkItem, WorkItemLink, QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingDataProvider, WorkItemId } from "Wit/UI/Data";

/**
 * Interface to work item grid column
 */
export interface IWorkItemTreeGridColumn {
  /**
   * A unique key for identifying the column.
   */
  key: string;
  /**
   * Name to render on the column header.
   */
  name: string;
  /**
   * The field to pull the text value from for the column. This can be null if a custom
   * onRender method is provided.
   */
  fieldName: string;
  /**
   * Minimum width for the column.
   */
  minWidth: number;
  /**
   * Optional maximum width for the column, if stretching is allowed in justified scenarios.
   */
  maxWidth?: number;
  /**
   * Optional initial width for the column
   */
  width?: ObservableValue<number>;
  /**
   * Optional accessibility label (aria-label) attribute that will be stamped on to the element.
   * If none is specified, the aria-label attribute will contain the column name
   */
  ariaLabel?: string;
  /**
   * Optional property to determine if the column can be resized.
   */
  isResizable?: boolean;
  /**
   * Optional action to render custom cell content, rather than the default rendering.
   */
  onRender?: (item: WorkItem | null, index?: number, column?: IWorkItemTreeGridColumn) => JSX.Element | null;
}

/**
 * Props for the WorkItemTreeGrid component
 */
export interface IWorkItemTreeGridProps {
  /**
   * Implementation of work item tracking data provider.
   *
   * Component uses it to fetch server resources for rendering.
   */
  dataProvider: IWorkItemTrackingDataProvider;
  /**
   * Items source to provide the items data to render.
   **/
  itemsSource: WorkItemId[] | WorkItem[];
  /**
   * Hierarchical relations between work items.
   */
  workItemRelations?: WorkItemLink[];

  queryType: QueryType;
  /**
   * Column definitions.
   */
  columns: IWorkItemTreeGridColumn[];
  /**
   * An optional class name to stick on the grid element.
   */
  className?: string;
  /**
   * Whether to display grid lines between rows.
   */
  showLines?: boolean;
  /**
   * Optional property to specify the number of work items to fetch per request.
   */
  pageSize?: number;
}